import { useState, useEffect, useCallback } from "react";
import THEME from "../../../theme";
import { fs } from "../utils/styles";
import { finanzenApi } from "../services/finanzenApi";

const geld = (n) => n == null ? "–" : `${n.toFixed(2)} €`;
const LEER_FORM = { bezeichnung: "", anschaffungskosten: "", anschaffungsdatum: "", nutzungsdauer_jahre: "5", abschreibung_prozent: "20", notiz: "" };

export default function AnlagevermoegenVerwaltung({ benutzerId }) {
  const [gueter, setGueter] = useState([]);
  const [offen, setOffen] = useState(false);
  const [formOffen, setFormOffen] = useState(false);
  const [bearbeiten, setBearbeiten] = useState(null);
  const [form, setForm] = useState(LEER_FORM);
  const [fehler, setFehler] = useState("");

  const laden = useCallback(() => {
    finanzenApi.anlagevermoegenListe(benutzerId).then(setGueter).catch(() => {});
  }, [benutzerId]);

  useEffect(() => { if (offen) laden(); }, [offen, laden]);

  const neuOeffnen = () => {
    setBearbeiten(null);
    setForm(LEER_FORM);
    setFormOffen(true);
    setFehler("");
  };

  const bearbeitenOeffnen = (g) => {
    setBearbeiten(g);
    setForm({
      bezeichnung: g.bezeichnung, anschaffungskosten: String(g.anschaffungskosten),
      anschaffungsdatum: g.anschaffungsdatum, nutzungsdauer_jahre: String(g.nutzungsdauer_jahre),
      abschreibung_prozent: String(g.abschreibung_prozent), notiz: g.notiz || "",
    });
    setFormOffen(true);
    setFehler("");
  };

  const speichern = async () => {
    if (!form.bezeichnung || !form.anschaffungskosten || !form.anschaffungsdatum) {
      setFehler("Bezeichnung, Anschaffungskosten und Anschaffungsdatum sind Pflicht.");
      return;
    }
    const body = {
      bezeichnung: form.bezeichnung,
      anschaffungskosten: parseFloat(form.anschaffungskosten),
      anschaffungsdatum: form.anschaffungsdatum,
      nutzungsdauer_jahre: parseFloat(form.nutzungsdauer_jahre),
      abschreibung_prozent: parseFloat(form.abschreibung_prozent),
      notiz: form.notiz || null,
    };
    try {
      if (bearbeiten) await finanzenApi.anlagegutBearbeiten(benutzerId, bearbeiten.id, { ...body, abgangsdatum: bearbeiten.abgangsdatum });
      else await finanzenApi.anlagegutAnlegen(benutzerId, body);
      setFormOffen(false);
      laden();
    } catch (e) {
      setFehler(e.message);
    }
  };

  const abgangSetzen = async (g) => {
    const datum = window.prompt(`Ab welchem Datum ist "${g.bezeichnung}" nicht mehr im Bestand (Verkauf/Entsorgung/Defekt)? Format JJJJ-MM-TT`, g.abgangsdatum || new Date().toISOString().slice(0, 10));
    if (datum === null) return;
    await finanzenApi.anlagegutAbgangSetzen(benutzerId, g.id, datum || null);
    laden();
  };

  const loeschen = async (g) => {
    if (!window.confirm(`"${g.bezeichnung}" wirklich komplett aus der Liste löschen?`)) return;
    await finanzenApi.anlagegutLoeschen(benutzerId, g.id);
    laden();
  };

  return (
    <div style={{ ...fs.karte, marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h3 style={{ color: THEME.colors.text, margin: 0 }}>🏋 Anlagevermögen (für Abschreibungs-Schätzung)</h3>
        <button style={{ ...fs.btnGray, width: "auto" }} onClick={() => setOffen(o => !o)}>{offen ? "▲ einklappen" : "▼ anzeigen"}</button>
      </div>
      {offen && (
        <>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>
            Geräte/Ausstattung mit Anschaffungskosten, Kaufdatum und linearem Abschreibungssatz – Grundlage für die Abschreibung in der Schätzungs-BWA oben.
            Ist ein Gerät verkauft, entsorgt oder kaputt, hier "Abgang setzen" statt löschen (Löschen entfernt es komplett aus der Liste).
          </p>
          <button style={{ ...fs.btn, width: "auto", marginBottom: 16 }} onClick={neuOeffnen}>+ Neues Anlagegut</button>

          {formOffen && (
            <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <h4 style={{ color: THEME.colors.text, marginTop: 0 }}>{bearbeiten ? "Anlagegut bearbeiten" : "Neues Anlagegut"}</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={fs.inputGroup}><label style={fs.label}>Bezeichnung *</label><input style={fs.input} value={form.bezeichnung} onChange={e => setForm(f => ({ ...f, bezeichnung: e.target.value }))} /></div>
                <div style={fs.inputGroup}><label style={fs.label}>Anschaffungskosten (€) *</label><input style={fs.input} type="number" step="0.01" value={form.anschaffungskosten} onChange={e => setForm(f => ({ ...f, anschaffungskosten: e.target.value }))} /></div>
                <div style={fs.inputGroup}><label style={fs.label}>Anschaffungsdatum *</label><input style={fs.input} type="date" value={form.anschaffungsdatum} onChange={e => setForm(f => ({ ...f, anschaffungsdatum: e.target.value }))} /></div>
                <div style={fs.inputGroup}><label style={fs.label}>Nutzungsdauer (Jahre)</label><input style={fs.input} type="number" value={form.nutzungsdauer_jahre} onChange={e => { const j = e.target.value; setForm(f => ({ ...f, nutzungsdauer_jahre: j, abschreibung_prozent: j ? (100 / parseFloat(j)).toFixed(2) : f.abschreibung_prozent })); }} /></div>
                <div style={fs.inputGroup}><label style={fs.label}>Abschreibung %/Jahr</label><input style={fs.input} type="number" step="0.01" value={form.abschreibung_prozent} onChange={e => setForm(f => ({ ...f, abschreibung_prozent: e.target.value }))} /></div>
                <div style={fs.inputGroup}><label style={fs.label}>Notiz</label><input style={fs.input} value={form.notiz} onChange={e => setForm(f => ({ ...f, notiz: e.target.value }))} /></div>
              </div>
              {fehler && <p style={{ color: THEME.colors.danger }}>{fehler}</p>}
              <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                <button style={{ ...fs.btn, width: "auto" }} onClick={speichern}>Speichern</button>
                <button style={{ ...fs.btnGray, width: "auto" }} onClick={() => setFormOffen(false)}>Abbrechen</button>
              </div>
            </div>
          )}

          <div style={fs.tabelle}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={fs.th}>Bezeichnung</th><th style={{ ...fs.th, textAlign: "right" }}>AHK</th>
                  <th style={fs.th}>Kauf</th><th style={{ ...fs.th, textAlign: "right" }}>%/Jahr</th>
                  <th style={{ ...fs.th, textAlign: "right" }}>€/Monat</th><th style={fs.th}>Status</th><th style={fs.th}></th>
                </tr>
              </thead>
              <tbody>
                {gueter.map(g => (
                  <tr key={g.id} style={g.abgangsdatum ? { opacity: 0.55 } : undefined}>
                    <td style={fs.td}>{g.bezeichnung}</td>
                    <td style={{ ...fs.td, textAlign: "right" }}>{geld(g.anschaffungskosten)}</td>
                    <td style={fs.td}>{g.anschaffungsdatum}</td>
                    <td style={{ ...fs.td, textAlign: "right" }}>{g.abschreibung_prozent}%</td>
                    <td style={{ ...fs.td, textAlign: "right" }}>{geld(g.monatlicher_abschreibungsbetrag)}</td>
                    <td style={fs.td}>{g.abgangsdatum ? `Abgang seit ${g.abgangsdatum}` : "aktiv"}</td>
                    <td style={{ ...fs.td, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button style={{ ...fs.btnGray, width: "auto", marginRight: 6 }} onClick={() => bearbeitenOeffnen(g)}>Bearbeiten</button>
                      <button style={{ ...fs.btnGray, width: "auto", marginRight: 6 }} onClick={() => abgangSetzen(g)}>{g.abgangsdatum ? "Abgang ändern" : "Abgang setzen"}</button>
                      <button style={fs.btnDanger} onClick={() => loeschen(g)}>Löschen</button>
                    </td>
                  </tr>
                ))}
                {gueter.length === 0 && <tr><td style={fs.td} colSpan={7}>Noch keine Anlagegüter erfasst.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
